import { useCallback } from 'react';
import {
  useGetUserPreferencesQuery,
  useGetSuggestedActionsQuery,
  useGetActionDefinitionsQuery,
  useUpdateSuggestedActionPreferenceMutation,
} from './api';
import type { ActionDefinitionData, SuggestedActionPreferenceData } from '../types';

/**
 * Hook for managing suggested action preferences
 * Provides a clean interface for the SuggestedActionPreferencesForm component
 */
export function useSuggestedActionPreferences() {
  const {
    data: actionDefinitionsResponse,
    isLoading: isLoadingDefinitions,
    error: definitionsError,
  } = useGetActionDefinitionsQuery();

  const {
    data: suggestedActionsResponse,
    isLoading: isLoadingPreferences,
    error: preferencesError,
  } = useGetSuggestedActionsQuery();

  const [updateActionPreference, { isLoading: isUpdating }] =
    useUpdateSuggestedActionPreferenceMutation();

  // Transform data for easier consumption
  const actionDefinitions: Record<string, ActionDefinitionData> =
    actionDefinitionsResponse?.actions || {};

  const preferences: Record<string, SuggestedActionPreferenceData> =
    suggestedActionsResponse?.suggested_actions || {};

  // Helper function to update a specific action preference
  const updateActionPreferenceById = useCallback(
    (actionId: string, enabled?: boolean) => {
      return updateActionPreference({
        actionId,
        updateSuggestedActionPreferenceRequest: { enabled },
      });
    },
    [updateActionPreference],
  );

  // Get enabled status for an action (with fallback to default)
  const getActionEnabledStatus = useCallback(
    (actionId: string): boolean => {
      const preference = preferences[actionId];
      const definition = actionDefinitions[actionId];
      return preference?.enabled ?? definition?.default_enabled ?? false;
    },
    [preferences, actionDefinitions],
  );

  return {
    // Data
    actionDefinitions,
    preferences,

    // Loading states
    isLoading: isLoadingDefinitions || isLoadingPreferences,
    isLoadingDefinitions,
    isLoadingPreferences,
    isUpdating,

    // Error states
    error: definitionsError || preferencesError,
    definitionsError,
    preferencesError,

    // Actions
    updateActionPreferenceById,
    getActionEnabledStatus,

    // Computed values
    actionIds: Object.keys(actionDefinitions),
    hasData: Object.keys(actionDefinitions).length > 0,
  };
}

/**
 * Hook for updating suggested action preferences
 * Following the pattern from actions feature
 */
export function useUpdateSuggestedActionPreference() {
  const [updateActionPreference, result] = useUpdateSuggestedActionPreferenceMutation();

  const mutate = useCallback(
    (
      actionId: string,
      enabled: boolean,
      options?: { onSuccess?: () => void; onError?: () => void },
    ) => {
      const promise = updateActionPreference({
        actionId,
        updateSuggestedActionPreferenceRequest: { enabled },
      });

      // Handle options manually since the hook doesn't directly expose them
      if (options) {
        promise
          .unwrap()
          .then(() => options.onSuccess?.())
          .catch(() => options.onError?.());
      }

      return promise;
    },
    [updateActionPreference],
  );

  return {
    mutate,
    ...result,
  };
}

/**
 * Hook for general user preferences (not just suggested actions)
 */
export function useUserPreferences() {
  const { data: userPreferencesResponse, isLoading, error, refetch } = useGetUserPreferencesQuery();

  const preferences = userPreferencesResponse?.preferences;

  return {
    preferences,
    userPreferences: preferences, // Alias for convenience
    subject: preferences?.subject,
    suggestedActionPreferences: preferences?.suggested_action_preferences,
    recordingPreferences: preferences?.recording_preferences,
    documentPreferences: preferences?.document_preferences,
    isLoading,
    error,
    refetch,
  };
}
